import { useCallback, useState } from "react";
import { ParcelSizeRange, useOwnershipLayerTypeContext } from "./Context";
import { OwnershipFilters } from "react-migration/domains/ownership/components/OwnershipFilters";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Checkbox, CheckboxState } from "react-migration/components/DeprecatedCheckbox";
import { Switch } from "react-migration/components/Switch";
import { OwnershipLegend } from "react-migration/domains/ownership/components/OwnershipLegend";
import { LayerTypeControlPageProps } from "../../types";
import clsx from "clsx";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { userStore } from "src/js/stores/user/store";
import { useControlTracking } from "../../ControlTrackingContext";

function useOwnershipControls() {
  const { debouncedTrackControlChange, trackControlChange } = useControlTracking();
  const { setParcelSizeRange, parcelSizeRange, setShowKnownOwners, showKnownOwners, tenureFilters } =
    useOwnershipLayerTypeContext();

  const handleKnownOwnersChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      debouncedTrackControlChange({ filterName: "knownOwners", filterState: event.target.checked });
      setShowKnownOwners(event.target.checked);
    },
    [setShowKnownOwners, debouncedTrackControlChange]
  );

  const handleParcelSizeRangeChange = useCallback(
    (parcelSize: ParcelSizeRange) => {
      debouncedTrackControlChange({
        filterName: "parcelSizeRange",
        filterState: parcelSize,
      });
      setParcelSizeRange(parcelSize);
    },
    [setParcelSizeRange, debouncedTrackControlChange]
  );

  const handleShowLeaseholdChange = useCallback(
    (v: boolean) => {
      trackControlChange({
        filterName: "showLeaseholds",
        filterState: v,
      });
      tenureFilters.setShowLeaseholds(v);
    },
    [tenureFilters, trackControlChange]
  );

  const handleShowFreeholdChange = useCallback(
    (v: boolean) => {
      trackControlChange({
        filterName: "showFreeholds",
        filterState: v,
      });
      tenureFilters.setShowFreeholds(v);
    },
    [tenureFilters, trackControlChange]
  );

  return {
    parcelSizeRange,
    showKnownOwners,
    tenureFilters,
    handleKnownOwnersChange,
    handleParcelSizeRangeChange,
    handleShowLeaseholdChange,
    handleShowFreeholdChange,
  };
}

export function OwnershipControlPage({ disabled }: LayerTypeControlPageProps) {
  const { t } = useTranslation();
  const {
    parcelSizeRange,
    showKnownOwners,
    tenureFilters,
    handleKnownOwnersChange,
    handleParcelSizeRangeChange,
    handleShowLeaseholdChange,
    handleShowFreeholdChange,
  } = useOwnershipControls();

  const {
    user: {
      settings: { unit },
    },
  } = useSnapshot(userStore);

  const [showKnownOwnersModal, setShowKnownOwnersModal] = useState<boolean>(false);
  const isUSUser = hasFeature(Feature.usAccess);

  const showTenureFilters = !isUSUser;

  return (
    <div className="space-y-4">
      {showTenureFilters && (
        <>
          <div className="space-y-2">
            <div className="relative flex items-center justify-between pb-4 border-b border-neutral-200">
              <label
                htmlFor="known-owners"
                className={clsx("text-sm font-medium flex! gap-x-1 items-center", {
                  "opacity-50": disabled,
                })}
              >
                <span>{t("ownership.react.map.sidebar.control_page.known_owners_toggle_label")}</span>
                <i
                  className="ml-1 text-lg cursor-pointer icon-lt-question-line text-text-base-tertiary"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowKnownOwnersModal(true);
                  }}
                />
              </label>
              <Switch
                label="Known Owners"
                id="known-owners"
                disabled={disabled}
                onToggle={handleKnownOwnersChange}
                value={showKnownOwners}
              />
            </div>
            <div className="relative pt-2 pb-4 space-y-2 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="freeholds"
                  className={clsx("block! text-sm font-medium", {
                    "opacity-50": disabled,
                  })}
                >
                  {t("ownership.react.map.sidebar.control_page.freehold_toggle_label")}
                </label>
                <Checkbox
                  id="freeholds"
                  onChange={handleShowFreeholdChange}
                  value={tenureFilters.showFreeholds ? CheckboxState.CHECKED : CheckboxState.EMPTY}
                  disabled={disabled}
                />
              </div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="leaseholds"
                  className={clsx("block! text-sm font-medium", {
                    "opacity-50": disabled,
                  })}
                >
                  {t("ownership.react.map.sidebar.control_page.leasehold_toggle_label")}
                </label>
                <Checkbox
                  id="leaseholds"
                  onChange={handleShowLeaseholdChange}
                  value={tenureFilters.showLeaseholds ? CheckboxState.CHECKED : CheckboxState.EMPTY}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
          <OwnershipLegend showModal={showKnownOwnersModal} setShowModal={setShowKnownOwnersModal} />
        </>
      )}
      <div className="space-y-2">
        {showTenureFilters && <div className={clsx("text-base", { "opacity-50": disabled })}>Filters</div>}
        <OwnershipFilters
          disabled={disabled}
          setParcelSizeRange={handleParcelSizeRangeChange}
          parcelSizeRange={parcelSizeRange}
          unitType={unit}
        />
      </div>
    </div>
  );
}
